import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDirection } from "../../../contexts/DirectionContext";
import AdvancedSearch from "../../common/AdvancedSearch";
import { VideoCarousel } from "../../video";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchContent,
  selectRegularVideos,
} from "../../../store/content/slice";
import { fetchAllTopics, selectAllTopics } from "../../../store/topics/slice";
import { CONTENT_SORT_VALUES } from "../../../constants/content";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../router/routes";

const Masthead: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const videos = useAppSelector(selectRegularVideos);
  const allTopics = useAppSelector(selectAllTopics);
  const [isTopicsExpanded, setIsTopicsExpanded] = useState(false);

  useEffect(() => {
    dispatch(
      fetchContent({
        type: "Video",
        sortBy: CONTENT_SORT_VALUES.CREATED_AT,
        sortType: "desc",
        limit: 4,
        page: 1
      })
    );
    // Fetch all topics when component mounts
    dispatch(fetchAllTopics());
  }, [dispatch]);

  // Get the topics to display based on expansion state
  const displayedTopics = isTopicsExpanded ? allTopics : allTopics.slice(0, 4);

  const toggleTopicsExpansion = () => {
    setIsTopicsExpanded(!isTopicsExpanded);
  };

  const handleSearch = (params: {
    query: string;
    topics: string[];
    duration: string;
    format: string;
  }) => {
    // Construct search URL with parameters
    const searchParams = new URLSearchParams();
    
    // Add search term if present
    if (params.query) {
      searchParams.set("term", params.query);
    }
    
    // Add topics only if there are actual topics selected
    if (params.topics && params.topics.length > 0) {
      searchParams.set("topics", params.topics.join(","));
    }
    
    // Add duration if specified
    if (params.duration) {
      searchParams.set("duration", params.duration);
    }
    
    // Add content type/format if specified
    if (params.format) {
      searchParams.set("contentType", params.format.toLowerCase());
    }

    // Navigate to search page with parameters
    navigate(`${ROUTES.SEARCH}?${searchParams.toString()}`);
  };

  const handleTopicClick = (topicId: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set("topics", topicId);
    searchParams.set("contentType", "video"); // Default to video content type for topic searches
    navigate(`${ROUTES.SEARCH}?${searchParams.toString()}`);
  };

  // Calculate margin class based on RTL
  const marginClass = isRTL
    ? "-ml-[calc(50vw-50%)]"
    : "-mr-[calc(50vw-50%)]";

  return (
    <div className="relative">
      {/* Hero Section */}
      <div className={`relative border-boverflow-hidden ${theme.mode === 'dark' ? 'bg-surface-primary' : 'bg-text-primary'}`}>
        {/* Background */}
        <div
          style={{
            background:
              theme.mode === "dark"
                ? `linear-gradient(to bottom, rgba(var(--colors-primary-500-rgb), 0.08), rgba(var(--colors-primary-900-rgb), 0.02))`
                : `linear-gradient(to bottom, var(--colors-text-primary), var(--colors-text-primary))`,
            boxShadow:
              theme.mode === "dark"
                ? `0 0 40px -10px rgba(var(--colors-primary-500-rgb), 0.2)`
                : `0 0 40px -10px rgba(var(--colors-text-primary-rgb), 0.3)`,
          }}
          className="absolute inset-0"
        />

        {/* Content Container */}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="pt-10 sm:pt-24 pb-12 sm:pb-32">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 justify-between items-start">
              {/* Title Section */}
              <div className="w-full lg:w-[500px]">
                <div className="mb-10 sm:mb-18">
                  <h1
                    className={`
                      text-3xl sm:text-5xl md:text-6xl font-major 
                      mb-2 sm:mb-4
                      leading-[1.1]
                      ${theme.mode === "dark"
                        ? "bg-gradient-to-r from-primary-300 to-primary-500 bg-clip-text text-transparent"
                        : "text-text-inverse"
                      }
                      text-center lg:text-start
                    `}
                  >
                    {t("common.appTitle")}
                  </h1>
                  <p
                    className={`
                      text-sm sm:text-lg md:text-xl 
                      font-light 
                      ${theme.mode === "dark"
                        ? "text-text-secondary"
                        : "text-text-inverse"
                      }
                      leading-tight
                      text-center lg:text-start
                    `}
                  >
                    {t("common.appSubtitle")}
                  </p>
                </div>

                {/* Search Section */}
                <div className="relative">
                  {/* Search Bar */}
                  <div className="mb-4 sm:mb-6">
                    <AdvancedSearch
                      onSearch={handleSearch}
                      className={`
                        w-full
                        ${theme.mode === "dark"
                          ? "bg-surface-primary bg-opacity-30"
                          : "bg-text-primary bg-opacity-50"
                        }
                        rounded-full
                        shadow-lg
                        ${theme.mode === "dark"
                          ? "shadow-primary-500 shadow-opacity-10"
                          : "shadow-background-primary shadow-opacity-90"
                        }
                        backdrop-blur-xl
                        transition-all duration-200
                        hover:shadow-xl
                        ${theme.mode === "dark"
                          ? "hover:shadow-primary-500 hover:shadow-opacity-10"
                          : "hover:shadow-surface-primary hover:shadow-opacity-90"
                        }
                      `}
                    />
                  </div>

                  {/* Featured Topics */}
                  <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start">
                    {displayedTopics.map((topic) => (
                      <span
                        key={topic.id}
                        onClick={() => handleTopicClick(topic.id)}
                        className={`
                          px-4 py-1.5
                          rounded-full
                          text-sm font-medium
                          ${theme.mode === "dark"
                            ? "bg-primary-900/20 text-primary-300 border-primary-700"
                            : "bg-white/5 text-text-inverse border-text-inverse/20"
                          }
                          border
                          transition-all duration-200
                          hover:bg-primary-500 hover:text-white hover:border-primary-500
                          cursor-pointer
                        `}
                      >
                        {topic.title}
                      </span>
                    ))}
                    {allTopics.length > 4 && (
                      <span
                        onClick={toggleTopicsExpansion}
                        className={`
                          px-4 py-1.5
                          rounded-full
                          text-sm font-medium
                          ${theme.mode === "dark"
                            ? "text-text-secondary opacity-60 border-surface-secondary/20"
                            : "text-text-secondary opacity-60 border-surface-secondary/10"
                          }
                          border
                          transition-colors duration-200
                          hover:opacity-90 
                          ${theme.mode === "dark"
                            ? "hover:text-background-primary hover:bg-text-primary"
                            : "hover:text-text-primary hover:bg-background-primary"
                          }
                          cursor-pointer
                          flex items-center gap-1
                        `}
                      >
                        {isTopicsExpanded ? t("common.viewLess") : t("common.viewMore")}
                        <Icon
                          icon={isTopicsExpanded 
                            ? "ph:arrow-up-thin"
                            : (isRTL ? "ph:arrow-left-thin" : "ph:arrow-right-thin")
                          }
                          className="w-4 h-4"
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Latest Videos */}
              <div className={`hidden lg:block w-full lg:w-[700px] relative ${marginClass} lg:sticky lg:[top:calc(var(--header-total-height)_+_1rem)]`}>
                <div
                  style={{
                    backgroundColor:
                      theme.mode === "dark"
                        ? "rgba(var(--colors-surface-primary-rgb), 0.5)"
                        : "rgba(var(--colors-background-primary-rgb), 0.09)",
                    border:
                      theme.mode === "dark"
                        ? "0.5px solid rgba(var(--colors-surface-secondary-rgb), 0.9)"
                        : "0.5px solid rgba(var(--colors-background-primary-rgb), 0.2)",
                  }}
                  className={`
                    relative
                    pt-8 pb-6
                    ${isRTL ? "pr-8" : "pl-8"}
                    ${isRTL ? "rounded-r-3xl" : "rounded-l-3xl"}
                    overflow-hidden
                    backdrop-blur-3xl
                    shadow-2xl
                    ${theme.mode === "dark" ? "shadow-primary-500/15" : "shadow-surface-primary/25"}
                    border
                    transform hover:scale-[1.01]
                    transition-all duration-300 ease-out
                    hover:shadow-3xl
                    hover:${theme.mode === "dark" ? "shadow-primary-500/20" : "shadow-surface-primary/30"}
                  `}
                >
                  <VideoCarousel
                    videos={videos.slice(0, 4)}
                    className="w-full"
                    autoScrollInterval={4000}
                    forceLeftToRight={true}
                    useMastheadStyle={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Masthead;
